// C10 : aucun cache, aucun rapprochement entre sens opposés, calcul en millimes.
// Un rapprochement de montants reste INDICATIF, jamais une preuve d'affectation.
export function calculerCoherence(snapshot, decompte) {
  if (snapshot?.source !== 'serveur_snapshot') throw new Error('Instantané serveur absent.');
  const d = snapshot.donnees || {};
  const fin = d['oliva.finances'];
  const receptions = d['oliva.receptions.v3'];
  const contacts = d['oliva.contacts'];
  const formats = d['oliva.formats'];
  if (!fin || !Array.isArray(fin.mouvements) || !Array.isArray(fin.reglements) ||
      !Array.isArray(receptions) || !Array.isArray(contacts) || !Array.isArray(formats))
    throw new Error('Données serveur absentes ou invalides : aucun verdict de cohérence possible.');
  const millimes = (v, contexte, arrondi = false) => {
    if (v === null || v === undefined || v === '' || typeof v === 'boolean')
      throw new Error(`${contexte} : montant absent ou invalide.`);
    const n = Number(v);
    if (!Number.isFinite(n) || Math.abs(n) > 1e9 || (!arrondi && Math.abs(n * 1000 - Math.round(n * 1000)) > 1e-5))
      throw new Error(`${contexte} : montant invalide.`);
    return Math.round(n * 1000);
  };
  const nom = (id) => {
    const c = contacts.find((x) => x.id === id);
    return c?.nom || c?.raisonSociale || id || 'Tiers absent';
  };
  const candidats = [];
  const alertes = [];
  for (const [bucket, rows] of [['mouvements', fin.mouvements], ['reglements', fin.reglements]]) {
    const vus = new Set();
    for (const x of rows) {
      if (!x.id || vus.has(x.id)) throw new Error(`Identifiant financier absent ou dupliqué : ${x.id || bucket}.`);
      vus.add(x.id);
      if (!x.tiersId || x.annule === true || x.annulee === true || x.annuleLe || x.annule_le) continue;
      if (bucket === 'reglements' && x.statut !== 'Payé') continue;
      if (bucket === 'mouvements' && !['Alimentation', 'Dépense'].includes(x.type)) continue;
      // Les contreparties d'autres opérations ne couvrent pas les bons de réception.
      if (x.ccaId || x.conversionId || x.venteId) continue;
      const sens = bucket === 'mouvements' && x.type === 'Alimentation' ? 'entree' : 'sortie';
      const montant = millimes(x.montant, `Écriture ${x.id}`);
      if (montant < 0) throw new Error(`Écriture ${x.id} : montant négatif à vérifier.`);
      candidats.push({ id: x.id, bucket, tiersId: x.tiersId, date: x.date, sens, montant, reste: montant });
    }
  }
  const lignes = [];
  for (const r of receptions.filter((x) => x.statut !== 'Annulé')) {
    const avance = millimes(r.avanceImputee ?? 0, `Avance ${r.id}`);
    const partiel = millimes(r.reglePartiel ?? 0, `Règlement partiel ${r.id}`);
    if (!r.regle && avance === 0 && partiel === 0) continue;
    if (!['client', 'fournisseur'].includes(r.parcours) || !r.tiersId)
      throw new Error(`Bon ${r.id} : parcours ou tiers absent.`);
    if (avance < 0 || partiel < 0) throw new Error(`Bon ${r.id} : imputation négative.`);
    // Vérifier les entrées du décompte existant : ne pas transformer un inconnu en zéro.
    for (const k of ['poidsBrut', 'tare']) millimes(r[k], `Bon ${r.id} / ${k}`, true);
    const calcul = decompte(r, formats);
    if (calcul.enAttenteRendement || calcul.provisoire)
      throw new Error(`Bon ${r.id} : montant non définitif, contrôle impossible.`);
    const total = millimes(calcul.montant, `Décompte ${r.id}`, true);
    const attendu = r.regle === true ? total : avance + partiel;
    if (attendu < 0 || avance + partiel > total) throw new Error(`Bon ${r.id} : imputation supérieure au montant dû.`);
    if (!attendu) { alertes.push(`Bon ${r.id} marqué réglé avec montant nul : à vérifier.`); continue; }
    lignes.push({id: r.id, date: r.date, parcours: r.parcours, tiersId: r.tiersId, tiers: nom(r.tiersId),
      regleDate: r.regleDate, regleMode: r.regleMode, regleRef: r.regleRef,
      sens: r.parcours === 'client' ? 'entree' : 'sortie', partiel: r.regle !== true,
      attendu, montant: attendu / 1000});
  }
  lignes.sort((a,b) => String(a.regleDate || a.date || '').localeCompare(String(b.regleDate || b.date || '')) || String(a.id).localeCompare(String(b.id)));
  const groupes = new Map();
  for (const l of lignes) {
    let manque = l.attendu;
    l.gages = [];
    for (const c of candidats.filter((x) => x.tiersId === l.tiersId && x.sens === l.sens && x.reste > 0)
      .sort((a,b) => Number(b.date === l.regleDate) - Number(a.date === l.regleDate))) {
      const pris = Math.min(manque, c.reste);
      if (!pris) break;
      c.reste -= pris; manque -= pris;
      l.gages.push(`${c.bucket} ${c.id} : ${(pris / 1000).toFixed(3)} TND (rapprochement indicatif)`);
    }
    l.manquant = manque / 1000; l.gage = manque === 0;
    const key = `${l.tiersId}:${l.sens}`;
    const g = groupes.get(key) || {key, tiersId: l.tiersId, sens: l.sens, nom: l.tiers, attendu: 0};
    g.attendu += l.attendu; groupes.set(key, g);
    delete l.attendu;
  }
  const tiers = [...groupes.values()].map((g) => {
    const reel = candidats.filter((c) => c.tiersId === g.tiersId && c.sens === g.sens).reduce((s,c) => s+c.montant, 0);
    const ecart = g.attendu - reel;
    return {...g, declare: g.attendu/1000, reel: reel/1000, ecart: ecart/1000,
      etat: ecart > 0 ? 'manque' : ecart < 0 ? 'excedent' : 'indicatif'};
  });
  const orphelins = lignes.filter((l) => !l.gage);
  return {source: snapshot.source, le: snapshot.le, versions: snapshot.versions, indicatif: true, alertes,
    lignes, orphelins, tiers,
    compteurs: {receptions: receptions.length, mouvements: fin.mouvements.length, reglements: fin.reglements.length},
    totaux: {nonGage: orphelins.reduce((s,l) => s+Math.round(l.manquant*1000),0)/1000,
      ecartNet: tiers.reduce((s,t) => s+Math.max(0,Math.round(t.ecart*1000)),0)/1000}};
}
